import { useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { map } from 'lodash';
import { OrthophotoSchema } from '@/types/types';
import { GlobeAltIcon } from '@heroicons/react/24/outline';
import { checkAuthorization } from '../../../utils/util';
import { useGetOrthophotosQuery } from '../../../features/orthophoto';
import { useGetProjectQuery } from '../../../features/project';
import { RootState } from '../../../store';
import Breadcrumbs from '../../global/Breadcrumbs';
import Table from '../../ui/data-display/Table';
import BoardContent from '../common/BoardContent';
import BoardEmpty from '../common/BoardEmpty';
import BoardHeader from '../common/BoardHeader';

export default function Orthophotos() {
  const { projectId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { user } = useSelector((state: RootState) => state.auth);

  const { data, isFetching } = useGetOrthophotosQuery({ projectId });
  const { data: project } = useGetProjectQuery({ projectId });

  const canUpsertOrthophoto = useMemo(() => checkAuthorization(user, project, 'orthophotos_upsert'), [user, project]);
  const hasData = useMemo(() => (data && data.length > 0), [data]);
  const isEmpty = useMemo(() => (data && data.length <= 0), [data]);
  const orthophotos = useMemo(() => [...map(data, o => ({...o, key: o.id}))], [data]);
  const isAnyLoading = useMemo<boolean>(() => (isFetching), [isFetching]);

  const columns = [
    {
      title: t('NAME'),
      key: 'name',
      render: (record: OrthophotoSchema) => (record.name),
    },
    {
      title: t('ACTIONS'),
      key: 'action',
      areActions: true,
      render: (record: OrthophotoSchema) => (
        <>
          {
            (canUpsertOrthophoto) &&
            <Link className="text-blue-600 hover:text-blue-900" to={`/board/${projectId}/${t('SLUG_ORTHOPHOTOS')}/${t('SLUG_EDIT')}/${record.id}`}>
              {t('EDIT')}
            </Link>
          }
        </>
      ),
    },
  ];

  return (
    <>
      <Breadcrumbs
        pages={[
          { name: `${t('PROJECT')} ${(project) ? project.name : ''}`, current: false },
          { name: t('ORTHOPHOTOS'), current: true },
        ]}
      />
      {
        (hasData) &&
        <BoardHeader
          title={t('ORTHOPHOTOS')}
          description={t('BOARD_ORTHOPHOTOS_PAGE_DESCRIPTION')}
          link={(canUpsertOrthophoto) ? `/board/${projectId}/${t('SLUG_ORTHOPHOTOS')}/${t('SLUG_ADD')}` : ''}
          linkText={(canUpsertOrthophoto) ? `${t('ADD')} ${t('ORTHOPHOTO')}` : ''}
        />
      }
      <BoardContent>
        {
          (hasData) &&
          <Table
            columns={columns}
            dataSource={orthophotos}
            isLoading={isAnyLoading}
          />
        }
        {
          (isEmpty) &&
          <BoardEmpty
            icon={<GlobeAltIcon className="mx-auto h-12 w-12 text-gray-400" />}
            title="Sin Ortofotos"
            description="Comienza creando una nueva ortofoto."
            buttonText="Nueva Ortofoto"
            buttonAction={() => { navigate(`/board/${projectId}/${t('SLUG_ORTHOPHOTOS')}/${t('SLUG_ADD')}`); }}
            canCreate={canUpsertOrthophoto}
          />
        }
      </BoardContent>
    </>
  );
}
